import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { profileDefinitions } from './profileDefinitions';
import Tooltip from '@mui/material/Tooltip';
import { isFreeUser } from './utils/planAccess';
import { LightBulbIcon } from '@heroicons/react/solid';

const trustMapping = {
    '11': 'lowCognitiveLowEmotion',
    '12': 'lowCognitiveMediumEmotion',
    '13': 'lowCognitiveHighEmotion',
    '21': 'mediumCognitiveLowEmotion',
    '22': 'mediumCognitiveMediumEmotion',
    '23': 'mediumCognitiveHighEmotion',
    '31': 'highCognitiveLowEmotion',
    '32': 'highCognitiveMediumEmotion',
    '33': 'highCognitiveHighEmotion',
};

function CreateScenarioForm() {
    const navigate = useNavigate();
    const location = useLocation();
    const isEdit = location.state?.isEdit || false; // Check if we're in edit mode
    const existingScenarioId = location.state?.scenarioId || null; // Retrieve scenarioId if in edit mode
    const [isLoading, setIsLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [missingFields, setMissingFields] = useState([]);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [isFreePlan, setIsFreePlan] = useState(false);
    const [scenarioCount, setScenarioCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingActionType, setPendingActionType] = useState(null);
    const [limitExceededMessage, setLimitExceededMessage] = useState('');
    const [originalTitle, setOriginalTitle] = useState('');
    const [calculatedDisc, setCalculatedDisc] = useState({
        label: '',
        description: '',
    });

    useEffect(() => {
        const fetchPlanAndCount = async () => {
            try {
                const planStatus = await isFreeUser();
                setIsFreePlan(planStatus);
            
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/scenario-count`, {
                    method: 'GET',
                    headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    },
                });
            
                if (response.ok) {
                    const { scenarioCount } = await response.json();
                    setScenarioCount(scenarioCount);
                }
            } catch (error) {
                console.error('Error fetching plan or scenario count:', error);
            } finally {
                setLoading(false);
            }
        };
        
        fetchPlanAndCount();
    }, []);    

    function ProfileTooltip({ profileKey }) {
        return (
            <Tooltip title={<span style={{ fontSize: '1rem' }}>{profileDefinitions[profileKey]}</span>}arrow>
                <LightBulbIcon
                    style={{
                        width: '18px',
                        height: '18px',
                        marginLeft: '5px',
                        cursor: 'pointer',
                        color: '#f59e0b', // Tailwind's amber-500 equivalent
                        backgroundColor: '#fef9c3', // Tailwind's yellow-100 equivalent
                        borderRadius: '50%',
                        padding: '2px',
                        display: 'inline-block',
                        verticalAlign: 'middle',
                    }}
                />
            </Tooltip>
        );
    }

    const baseDefaults = {
        employeeName: '',
        imageName: '',
        voiceSelection: 'male',
        employeeAge: '',
        jobTitle: '',
        yearsAtCompany: '0-6 months',
        yearsInCurrentJob: '0-6 months',
        totalExperience: 'Less than 1 year',
        jobResponsibilities: '',
        scenarioTitle: '',
        performanceIssueSummary: '',
        performanceIssues: '',
        performanceIssuesOther: '',
        priorDiscussions: 'No prior discussion has taken place.',
        interpersonalIssues: '',
        interpersonalIssuesOther: '',
        specificExamples: '',
        autoGenerateExamples: false,
        additionalInfo: '',
        otherRelevantInfo: '',
        employeeUnderstanding: 'Fully understands the issue and its impact.',
        employeeFeelings: '',
        employeeFeelingsOther: '',
        teamRelationships: 'Works well with everyone and is a positive team member.',
        personalIssues: '',
        personalIssuesOther: '',
        otherEmployeeDetails: '',
        familiarity: 'low',
        emotion: 'low',
        trust: 'lowCognitiveLowEmotion',
        trustCognition: 'low',
        trustCognitionNumeric: '1',
        trustEmotion: 'low',
        trustEmotionNumeric: '1',
        values: 'selfMeansSelfEnds',
        valuesMeans: 'self',
        valuesEnds: 'self',
        ew: 3,
        ac: 3,
        ei: 3,
        di: 3,
        pj: 3,
        rt: 3,
        op: 3,
        co: 3,
        tp: 3,
      };
      
    const freeUserOverrides = {
        familiarity: 'medium',
        emotion: 'medium',
        trust: 'mediumCognitiveMediumEmotion',
        trustCognition: 'medium',
        trustCognitionNumeric: '2',
        trustEmotion: 'medium',
        trustEmotionNumeric: '2',
    };
      
    const [formData, setFormData] = useState(baseDefaults);

    useEffect(() => {
        if (isFreePlan) {
          setFormData((prev) => ({ ...prev, ...freeUserOverrides }));
        }
    }, [isFreePlan]);

    const fieldLabelMapping = {
        scenarioTitle: "Scenario Title",
        employeeName: "Employee Name",
        employeeAge: "Approximate Age",
        jobTitle: "Job Title",
        performanceIssueSummary: "General Summary of Conversation Topic or Issue",
        employeeFeelings: "How Employee Feels About Situation",
        interpersonalIssues: "Interpersonal Issues Between Employee and Team Members",
        employeeFeelingsOther: "Other Textbox for Employee Feelings",
        interpersonalIssuesOther: "Other Textbox for Interpersonal Issues"
    };
    
    //----------------------------------
    //     Form Validation Code
    //----------------------------------
    const handleFormValidation = (actionType) => {
        const requiredFields = ['scenarioTitle', 'employeeName', 'employeeAge', 'jobTitle', 'performanceIssueSummary'];
        const requiredCheckboxGroups = ['employeeFeelings', 'interpersonalIssues'];
    
        let missingFieldErrors = {};

        console.log("🚀 Validating Form Data:", formData);

        // Find missing text fields
        requiredFields.forEach(field => {
            if (!formData[field]?.trim()) {
                missingFieldErrors[field] = true;
            }
        });
    
        // Find missing checkbox groups
        requiredCheckboxGroups.forEach(group => {
            if (!formData[group] || formData[group].length === 0) {
                missingFieldErrors[group] = true;
            }
        });
    
        // Check "Other" fields validation
        requiredCheckboxGroups.forEach(group => {
            if (formData[group]?.includes("Other")) {
                const otherField = `${group}Other`; // Dynamically get the "Other" text field key
                if (!formData[otherField]?.trim()) {
                    missingFieldErrors[otherField] = true;
                }
            }
        });
    
        // If any errors exist, store them in state and stop execution
        if (Object.keys(missingFieldErrors).length > 0) {
            console.log("❌ Missing Fields:", missingFieldErrors);
            setMissingFields(missingFieldErrors);
            return; // ✅ Stop execution if there are missing fields
        }

        // ✅ If validation passes, clear errors and proceed with the correct function
        setMissingFields({});

        if (actionType === 'new' && isEdit && formData.scenarioTitle.trim() === originalTitle.trim()) {
            setPendingActionType('confirm-rename');
            setShowConfirmModal(true);
            return;
          }

        setPendingActionType(actionType);
        setShowConfirmModal(true); 
    };
    //---------------------------------

    const options = {
        familiarity: [
            { value: 'low', label: 'Low', description: 'You and the employee have just met or have very few interactions.' },
            { value: 'medium', label: 'Medium', description: 'You and the employee have interacted enough to start understanding each other.' },
            { value: 'high', label: 'High', description: 'You and the employee have a long-standing relationship with deep understanding.' }
        ],
        emotion: [
            { value: 'low', label: 'Low', description: 'The employee shows minimal emotional expression.' },
            { value: 'medium', label: 'Medium', description: 'The employee shows a moderate level of emotional expression.' },
            { value: 'high', label: 'High', description: 'The employee shows significant emotional expression.' }
        ],
        trustCognitive: [
            { value: 'low', label: 'Low', description: 'Low logical trust means minimal confidence in skills, abilities, or judgment.' },
            { value: 'medium', label: 'Medium', description: 'Moderate logical trust reflects some confidence in skills and judgment.' },
            { value: 'high', label: 'High', description: 'High logical trust means strong confidence in skills, abilities, and decision-making.' }
          ],
        trustEmotion: [
            { value: 'low', label: 'Low', description: 'Low emotional trust indicates a weak personal connection, with little emotional rapport.' },
            { value: 'medium', label: 'Medium', description: 'Medium emotional trust reflects some emotional rapport and mutual understanding.' },
            { value: 'high', label: 'High', description: 'High emotional trust suggests a strong personal bond and emotional connection.' }
          ],        
        disc: [
            { value: 'D', label: 'D', description: 'Dominance: High emphasis on shaping the environment by overcoming opposition to accomplish results.' },
            { value: 'I', label: 'I', description: 'Influence: High emphasis on influencing or persuading others.' },
            { value: 'S', label: 'S', description: 'Steadiness: High emphasis on cooperation and sincerity, values loyalty.' },
            { value: 'C', label: 'C', description: 'Compliance: High emphasis on quality, accuracy, expertise, and competency.' },
            { value: 'DI', label: 'Di', description: 'Combination of Dominance and Influence: Assertive, persuasive, and outgoing.' },
            { value: 'DS', label: 'Ds', description: 'Combination of Dominance and Steadiness: Assertive, deliberate, and methodical.' },
            { value: 'DC', label: 'Dc', description: 'Combination of Dominance and Compliance: Assertive, challenging, and objective.' },
            { value: 'ID', label: 'Id', description: 'Combination of Influence and Dominance: Persuasive, friendly, and assertive.' },
            { value: 'IS', label: 'Is', description: 'Combination of Influence and Steadiness: Enthusiastic, supportive, and friendly.' },
            { value: 'IC', label: 'Ic', description: 'Combination of Influence and Compliance: Persuasive, friendly, and precise.' },
            { value: 'SD', label: 'Sd', description: 'Combination of Steadiness and Dominance: Methodical, deliberate, and assertive.' },
            { value: 'SI', label: 'Si', description: 'Combination of Steadiness and Influence: Amiable, supportive, and friendly.' },
            { value: 'SC', label: 'Sc', description: 'Combination of Steadiness and Compliance: Methodical, systematic, and stable.' },
            { value: 'CD', label: 'Cd', description: 'Combination of Compliance and Dominance: Cautious, precise, and assertive.' },
            { value: 'CI', label: 'Ci', description: 'Combination of Compliance and Influence: Precise, systematic, and friendly.' },
            { value: 'CS', label: 'Cs', description: 'Combination of Compliance and Steadiness: Systematic, stable, and precise.' }
        ], 
        valuesMeans: [
            { value: 'self', label: 'Self', description: 'Self Means indicates that individual actions are prioritized. Personal initiative and autonomy are emphasized.' },
            { value: 'others', label: 'Others', description: 'Other Means indicates a reliance on others’ actions, teamwork, or collaboration to achieve results.'}
        ],
        valuesEnds: [
            { value: 'self', label: 'Self', description: 'Self Ends reflects a focus on achieving personal goals, benefits, or recognition.' },
            { value: 'others', label: 'Others', description: 'Other Ends reflects a focus on benefiting the team, organization, or collective mission.' }
        ]
    }

    const sliderOptions = {
        ew: {
            1: { label: 'High Engage (Fight)', description: 'Highly engaged in interactions, proactively addressing challenges and conflicts.' },
            2: { label: 'Engage (Fight)', description: 'Prefers engaging with challenges directly, focusing on active participation.' },
            3: { label: 'Balanced', description: 'Balances engaging and withdrawing behaviors, depending on the situation.' },
            4: { label: 'Withdraw (Flight)', description: 'Prefers stepping back from direct confrontation, focusing on avoiding conflict.' },
            5: { label: 'High Withdraw (Flight)', description: 'Highly withdrawn from interactions, avoiding conflict or confrontation.' },
        },
        ac: {
            1: { label: 'Highly Accepting', description: 'Strongly adapts to the environment, prioritizing harmony and creative use of resources.' },
            2: { label: 'Accepting', description: 'Prefers to work within established conditions and adapt to existing structures.' },
            3: { label: 'Balanced', description: 'Balances acceptance and control, adapting as needed or asserting when required.' },
            4: { label: 'Controlling', description: 'Prefers to shape their environment to achieve their desired outcomes.' },
            5: { label: 'Highly Controlling', description: 'Strongly prefers to act on (or change) the environment to meet their desired outcomes.' },
        },
        ei: {
            1: { label: 'Highly Extroverted', description: 'Thrives on social interaction, expressing thoughts and emotions openly.' },
            2: { label: 'Extroverted', description: 'Leans toward extroversion, engaging easily in social settings and sharing ideas.' },
            3: { label: 'Balanced', description: 'Balances extroverted and introverted tendencies, adapting to the context.' },
            4: { label: 'Introverted', description: 'Prefers introspection, expressing thoughts selectively in quieter environments.' },
            5: { label: 'Highly Introverted', description: 'Deeply values introspection and privacy, often avoiding social interactions.' },
        },
        di: {
            1: { label: 'Highly Direct', description: 'Highly values clarity and brevity, prioritizing straightforward communication.' },
            2: { label: 'Direct', description: 'Favors direct communication, expressing thoughts clearly and concisely.' },
            3: { label: 'Balanced', description: 'Balances direct and indirect communication, adapting to the audience.' },
            4: { label: 'Indirect', description: 'Takes a roundabout approach, valuing context and detail in communication.' },
            5: { label: 'Highly Indirect', description: 'Prefers detailed and subtle communication, relying on context and nuance.' },
        },
        pj: {
            1: { label: 'Highly Perceiving', description: 'Highly open to new ideas, embracing adaptability and multiple perspectives.' },
            2: { label: 'Perceiving', description: 'Values flexibility and gathering input from various sources before deciding.' },
            3: { label: 'Balanced', description: 'Balances perceiving and judging tendencies, adapting to situational demands.' },
            4: { label: 'Judging', description: 'Emphasizes structure and decisiveness in evaluating and acting on situations.' },
            5: { label: 'Highly Judging', description: 'Strongly favors structure and decisive approaches, acting on well-defined expectations.' },
        },
        rt: {
            1: { label: 'Highly Risk-Taking', description: 'Highly adventurous and eager to explore new opportunities, embracing uncertainty.' },
            2: { label: 'Risk-Taking', description: 'Prefers action and discovery, focusing on learning through experience.' },
            3: { label: 'Balanced', description: 'Balances risk-taking and risk-assessing approaches based on situational needs.' },
            4: { label: 'Risk-Assessing', description: 'Carefully considers actions, ensuring understanding before proceeding.' },
            5: { label: 'Highly Risk-Assessing', description: 'Strongly cautious and meticulous, avoiding unnecessary risks and prioritizing planning.' },
        },
        op: {
            1: { label: 'Highly Optimistic', description: 'Consistently positive, viewing challenges as opportunities and expecting favorable outcomes.' },
            2: { label: 'Optimistic', description: 'Leans toward optimism, showing hopefulness and confidence despite challenges.' },
            3: { label: 'Balanced', description: 'Balances optimism and pessimism, adapting perspective based on the situation.' },
            4: { label: 'Pessimistic', description: 'Focuses on potential downsides, approaching situations with caution and skepticism.' },
            5: { label: 'Highly Pessimistic', description: 'Deeply cautious and skeptical, often expecting unfavorable outcomes.' },
        },
        co: {
            1: { label: 'Highly Change-Oriented', description: 'Thrives on novelty and actively seeks innovative solutions and opportunities for change.' },
            2: { label: 'Change-Oriented', description: 'Shows a preference for adapting and exploring new ideas.' },
            3: { label: 'Balanced', description: 'Balances change and continuity, adapting as necessary while appreciating stability.' },
            4: { label: 'Continuity-Oriented', description: 'Prefers consistent and predictable environments, maintaining routines.' },
            5: { label: 'Highly Continuity-Oriented', description: 'Strongly values tradition and stability, avoiding unnecessary disruptions.' },
        },
        tp: {
            1: { label: 'Highly Task-Focused', description: 'Prioritizes efficiency, productivity, and achieving goals above personal interactions or relationships.' },
            2: { label: 'Task-Focused', description: 'Shows a preference for task-oriented activities, focusing on objectives and results while considering interpersonal dynamics.' },
            3: { label: 'Balanced', description: 'Balances task and people focus, valuing both achieving goals and fostering positive relationships.' },
            4: { label: 'People-Focused', description: 'Shows a preference for people-oriented activities, emphasizing relationships and harmony while working toward objectives.' },
            5: { label: 'Highly People-Focused', description: 'Places strong importance on relationships, collaboration, and fostering a sense of community over achieving specific task-related goals.' },
        },
    };
    
    const sliderValueToTermMap = {
        ew: {
            1: 'heng',  // High Engage
            2: 'eng',   // Engage
            3: 'mew',   // Middle Engage/Withdraw
            4: 'with',  // Withdraw
            5: 'hwith', // High Withdraw
        },
        ac: {
            1: 'hac',   // High Accepting
            2: 'acc',   // Accepting
            3: 'mac',   // Middle Accept/Control
            4: 'con',   // Controlling
            5: 'hcon',  // High Controlling
        },
        ei: {
            1: 'hext',  // High Extrovert
            2: 'ext',   // Extrovert
            3: 'mextint', // Middle Extrovert/Introvert
            4: 'int',   // Introvert
            5: 'hint',  // High Introvert
        },
        di: {
            1: 'hdir',  // High Direct
            2: 'dir',   // Direct
            3: 'mdirind', // Middle Direct/Indirect
            4: 'ind',   // Indirect
            5: 'hind',  // High Indirect
        },
        pj: {
            1: 'hper',  // High Perceive
            2: 'per',   // Perceive
            3: 'mperjud', // Middle Perceive/Judge
            4: 'jud',   // Judge
            5: 'hjud',  // High Judge
        },
        rt: {
            1: 'hrt',   // High Risk-Taking
            2: 'rt',    // Risk-Taking
            3: 'mrtra', // Middle Risk-Taking/Risk-Assessing
            4: 'ra',    // Risk-Assessing
            5: 'hra',   // High Risk-Assessing
        },
        op: {
            1: 'hopt',  // High Optimist
            2: 'opt',   // Optimist
            3: 'moptpes', // Middle Optimist/Pessimist
            4: 'pes',   // Pessimist
            5: 'hpes',  // High Pessimist
        },
        co: {
            1: 'hcha',  // High Change-Oriented
            2: 'cha',   // Change-Oriented
            3: 'mchacon', // Middle Change/Continuity-Oriented
            4: 'con',   // Continuity-Oriented
            5: 'hcon',  // High Continuity-Oriented
        },
        tp: {
            1: 'htf', //High Task-Focused
            2: 'tf', //Task-Focused
            3: 'mtfpf', //Middle Task/People-Focused
            4: 'pf', //People-Focused
            5: 'hpf', //High People-Focused
        }
    };

    const imageList = [
        'Work Female Portrait 1_thumb.png',
        'Work Female Portrait 2_thumb.png',
        'Work Female Portrait 3_thumb.png',
        'Work Female Portrait 4_thumb.png',
        'Work Female Portrait 5_thumb.png',
        'Work Female Portrait 6_thumb.png',
        'Work Female Portrait 7_thumb.png',
        'Work Female Portrait 8_thumb.png',
        'Work Female Portrait 9_thumb.png',
        'Work Female Portrait 10_thumb.png',
        'Work Female Portrait 11_thumb.png',
        'Work Female Portrait 12_thumb.png',
        'Work Female 1_thumb.png',
        'Work Female 2_thumb.png',
        'Work Female 3_thumb.png',
        'Work Female 4_thumb.png',
        'Work Female 5_thumb.png',
        'Work Male 1_thumb.png',
        'Work Male 2_thumb.png',
        'Work Male 3_thumb.png',
        'Work Male 4_thumb.png',
        'Work Male 5_thumb.png',
        'Work Male 6_thumb.png',
        'Work Male 7_thumb.png',
        'Work Male 8_thumb.png',
        'Work Male Portrait 1_thumb.png',
        'Work Male Portrait 2_thumb.png',
        'Work Male Portrait 3_thumb.png',
        'Work Male Portrait 4_thumb.png',
        'Work Male Portrait 5_thumb.png',
        'Work Male Portrait 6_thumb.png',
        'Work Male Portrait 7_thumb.png',
        'Work Male Portrait 8_thumb.png',
        'Work Male Portrait 9_thumb.png',
    ];

    const valuesMapping = {
        'selfself': 'selfMeansSelfEnds',
        'selfothers': 'selfMeansOtherEnds',
        'othersself': 'otherMeansSelfEnds',
        'othersothers': 'otherMeansOtherEnds',
    };

    // Fetch existing data if in edit mode
    useEffect(() => {
        if (isEdit && existingScenarioId) {
            const fetchScenarioData = async () => {
                try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/scenarios/getScenarioForm/${existingScenarioId}`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        }
                    });
                    if (response.ok) {
                        const data = await response.json();

                        setOriginalTitle(data.scenarioTitle || '');

                        // Reverse mapping: Find the numeric keys for the fetched trust value
                        const reverseTrustMapping = Object.entries(trustMapping).reduce((acc, [key, value]) => {
                            acc[value] = key;
                            return acc;
                        }, {});

                        const numericTrust = reverseTrustMapping[data.trust]; // e.g., '22' for 'mediumCognitiveMediumEmotion'
                        const cognition = numericTrust?.charAt(0); // First digit for cognition
                        const emotion = numericTrust?.charAt(1); // Second digit for emotion

                        setFormData(prevData => ({
                            ...prevData,
                            ...data,  // Only update fields that exist in both prevData and data
                            trustCognition: cognition === '1' ? 'low' : cognition === '2' ? 'medium' : 'high',
                            trustEmotion: emotion === '1' ? 'low' : emotion === '2' ? 'medium' : 'high',
                            trustCognitionNumeric: cognition || '1', // Default to '1' if undefined
                            trustEmotionNumeric: emotion || '1',    // Default to '1' if undefined

                        }));
                    } else {
                        console.error('Failed to fetch scenario data');
                    }
                } catch (error) {
                    console.error('Error fetching scenario data:', error);
                }
            };

            fetchScenarioData();
        }
    }, [isEdit, existingScenarioId]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        let newValue = value;

        // ✅ Apply special validation only for employeeName
        if (name === "employeeName") {
            newValue = value.replace(/[^\w\s\-.,'"]/gi, ''); // Removes emojis & unwanted characters
        }

        setFormData(prev => ({ ...prev, [name]: newValue }));
    };

    // Handles checkboxes and stores as a semicolon-separated string
    const handleCheckboxChange = (e) => {
        const { name, value, checked } = e.target;
    
        setFormData((prev) => {
            let updatedValues = prev[name] ? prev[name].split("; ").filter(Boolean) : []; // Convert string to array
    
            if (checked) {
                updatedValues.push(value); // Add checked value
            } else {
                updatedValues = updatedValues.filter(item => item !== value); // Remove unchecked value
            }
    
            return { ...prev, [name]: updatedValues.join("; ") }; // Convert array back to semicolon-separated string
        });
    };

    //function to change time in current job value if time at company is changed to lower value
    const handleCompanyChange = (e) => {
        const newCompanyValue = e.target.value;
        setFormData((prev) => {
            // Define the valid options in order
            const companyOptions = ['0-6 months', '6-12 months', '1-2 years', '2-4 years', '4-8 years', '8+ years'];
            const newCompanyIndex = companyOptions.indexOf(newCompanyValue);
            const currentJobIndex = companyOptions.indexOf(prev.yearsInCurrentJob);
    
            // If current job selection is now invalid, set it to the highest allowed option
            const newJobValue = currentJobIndex > newCompanyIndex ? newCompanyValue : prev.yearsInCurrentJob;
    
            return { ...prev, yearsAtCompany: newCompanyValue, yearsInCurrentJob: newJobValue };
        });
    };

    const addThumbToFilename = (filename) =>
        filename.replace(/(\.[^/.]+)$/, '_thumb$1');

    // Handle submission for creating a new scenario
    const handleCreateNewScenario = async (e) => {

        const token = localStorage.getItem('token'); // Retrieve token
        if (!token) {
            console.error('No token available');
            alert('You are not logged in. Please log in to continue.');
            return;
        }
        const useGemini = localStorage.getItem('useGemini') === 'true';

        setIsLoading(true);
        setProgress(0);
        let softProgress = 0; // Track progress incrementally

        // Start soft progress update
        const interval = setInterval(() => {
            if (softProgress < 95) { // Limit soft progress to avoid going beyond hard progress
                setProgress((prev) => Math.min(prev + 1, 95));
                softProgress++;
            }
        }, 500); // Increment every 500ms

        try {
            // Remove any existing _id or scenarioId to ensure a fresh entry
            const newScenarioData = { ...formData };
            delete newScenarioData._id; // Remove _id if it exists
            delete newScenarioData.scenarioId; // Remove scenarioId if it exists

            console.log("🚀 Sending Scenario Data to Backend:", newScenarioData);

            // Step 1: Submit form data to the backend
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/scenarios/save-scenario-form`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(formData),
            });

            if (response.status === 429) {
                const { message } = await response.json();
                setIsLoading(false);
                setLimitExceededMessage(message); // Show modal or banner
                return;
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const { scenarioId } = await response.json(); // Get the scenarioId from the response

            console.log("✅ New scenarioId received from backend:", scenarioId);

            await new Promise(resolve => setTimeout(resolve, 1500));

            // Step 2: Start EventSource to track progress
            const eventSource = new EventSource(`${process.env.REACT_APP_API_BASE_URL}/api/scenarios/create-scenario-progress/${scenarioId}?token=${token}&useGemini=${useGemini}`);

            eventSource.onmessage = (event) => {
                const data = JSON.parse(event.data);
                const hardProgress = data.progress;

                // Update progress bar to sync with backend updates
                if (hardProgress > softProgress) {
                    setProgress(hardProgress);
                    softProgress = hardProgress; // Sync softProgress to backend progress
                }

                if (hardProgress === 100) {
                    // Scenario creation complete
                    eventSource.close();
                    clearInterval(interval); // Clear soft progress interval
                    setProgress(100);
                    setIsLoading(false);
                    navigate('/custom-scenarios'); // Redirect after completion
                }
            };

            eventSource.onerror = (error) => {
                console.error('Error with EventSource:', error);
                eventSource.close();
                setIsLoading(false);
                alert('An error occurred while creating the scenario.');
            };
        } catch (error) {
            console.error('Error during scenario creation:', error);
            setIsLoading(false);
            alert('Failed to create scenario.');
        }
    };

    // Handle submission for updating an existing scenario
    const handleUpdateExistingScenario = async (e) => {

        const useGemini = localStorage.getItem('useGemini') === 'true';
        
        setIsLoading(true);
        setProgress(0);
        let softProgress = 0;
    
        // Start soft progress
        const interval = setInterval(() => {
            if (softProgress < 95) {
                setProgress((prev) => Math.min(prev + 1, 95));
                softProgress++;
            }
        }, 500); // Increment every 500ms

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No token available');
            }

            // Remove helper fields (e.g., trustCognition, trustEmotion, etc.) before sending the payload
            const updatedScenarioData = { ...formData };
            delete updatedScenarioData.trustCognition; // Remove Cognition helper field
            delete updatedScenarioData.trustCognitionNumeric; // Remove Cognition numeric field
            delete updatedScenarioData.trustEmotion; // Remove Emotion helper field
            delete updatedScenarioData.trustEmotionNumeric; // Remove Emotion numeric field

            // Step 1: Save the updated form data to the backend
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/scenarios/save-updated-scenario/${existingScenarioId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(updatedScenarioData)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const { hasOnlyNonPromptChanges } = await response.json();

            const eventSource = new EventSource(`${process.env.REACT_APP_API_BASE_URL}/api/scenarios/update-scenario-progress/${existingScenarioId}?token=${token}&hasOnlyNonPromptChanges=${hasOnlyNonPromptChanges}&useGemini=${useGemini}`);

            eventSource.onmessage = (event) => {
                const data = JSON.parse(event.data);
                const hardProgress = data.progress;

                if (hardProgress > softProgress) {
                    setProgress(hardProgress);
                    softProgress = hardProgress;
                }

                if (hardProgress === 100) {
                    eventSource.close();
                    clearInterval(interval);
                    setProgress(100);
                    setIsLoading(false);
                    navigate('/custom-scenarios'); // Redirect after completion
                }
            };

            eventSource.onerror = (error) => {
                console.error('Error with EventSource:', error);
                eventSource.close();
                clearInterval(interval);
                setIsLoading(false);
                alert('An error occurred while updating the scenario.');
            };
        } catch (error) {
            console.error('Error during scenario update:', error);
            clearInterval(interval);
            setIsLoading(false);
            alert('Failed to update scenario.');
        }
    };

    const handleCalculateDISC = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                alert('You must be logged in to calculate DISC.');
                return;
            }

        // Translate slider values to terms using the mapping table
        const modes = {};
        Object.keys(sliderValueToTermMap).forEach((key) => {
            const sliderValue = formData[key]; // Get the numeric value from formData (1-5)
            modes[key] = sliderValueToTermMap[key][sliderValue]; // Map it to its term (e.g., heng, eng)
        });

            // Make the API call
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/scenarios/calculate-disc`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ modes }),
            });

            if (response.ok) {
                const data = await response.json();
                const returnedDisc = data.disc; // Update the calculated DISC in the state

                      // Map the DISC result to the options table
                const matchedDisc = options.disc.find(option => option.value === returnedDisc);

                if (matchedDisc) {
                    setCalculatedDisc({
                        label: matchedDisc.label,
                        description: matchedDisc.description,
                    });
                } else {
                    setCalculatedDisc({
                        label: returnedDisc,
                        description: 'No description available for this DISC type.',
                    });
                }

            } else {
                const errorData = await response.json();
                console.error('Failed to calculate DISC:', errorData.error);
                alert('Failed to calculate DISC. Please try again.');
            }
        } catch (error) {
            console.error('Error calculating DISC:', error);
            alert('An error occurred while calculating DISC.');
        }
    };

    // Define inline styles for slider elements
    const styles = {
        description: {
            fontSize: '16px',
            color: '#666',
            marginBottom: '10px'
        },
        slider: {
            width: '100%',
            margin: '10px 0',
            height: '6px',
            borderRadius: '10px',
            appearance: 'none', // Remove default browser styles
            outline: 'none',
            position: 'relative',
        },
        tickContainer: {
            position: 'absolute',
            width: '100%',
            height: '10px',
            top: '22px', // Position tick marks below the slider
            display: 'flex',
            justifyContent: 'space-between',
        },
        tick: {
            position: 'absolute',
            bottom: '-8px', // Adjust vertical position relative to the slider
            fontSize: '16px', // Tick mark size
            color: '#333', // Tick mark color
            transform: 'translateX(-50%)', // Center the tick mark at its position
        },
    };

    //-------------------------------------
    // Modal for image selection
    //-------------------------------------
    const ImageSelectionModal = ({ images, selectedImage, onSelect, onCancel }) => {
        const [highlightedImage, setHighlightedImage] = useState(selectedImage);
        const [loading, setLoading] = useState(true); // Track loading state
        const [loadedImages, setLoadedImages] = useState([]);

        useEffect(() => {
            const loadImages = async () => {
                setLoading(true);
        
                // Create promises for loading all images
                const imagePromises = images.map((image) => {
                    return new Promise((resolve) => {
                        const img = new Image();
                        img.src = `/Profile_Pictures/${image}`; // Path to the thumbnail
        
                        img.onload = () => resolve(image);
                        img.onerror = () => resolve(image);
                    });
                });
        
                // Wait for all image loading to complete
                await Promise.all(imagePromises);
        
                // Update state after all images are loaded
                setLoadedImages(images);
                setLoading(false);
            };
        
            loadImages();
        }, [images]);
    
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
                <div className="bg-white p-5 rounded text-center max-h-[70vh] max-w-[80vw] w-[500px] flex flex-col overflow-hidden">
                    <h2 className="text-lg font-bold mb-2">Select a Profile Picture</h2>
                    {loading ? (
                        <div className="text-lg text-gray-500">
                            Loading...
                            <div className="flex justify-center items-center h-full">
                                <div className="border-4 border-gray-300 border-t-purple-700 rounded-full w-10 h-10 animate-spin"></div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex-1 overflow-y-auto grid grid-cols-3 gap-4 py-2">
                            {/* No Picture Option */}
                            <div
                                className={`w-[140px] h-[100px] flex justify-center items-center text-xs cursor-pointer rounded 
                                    ${highlightedImage === '' 
                                        ? 'text-green-600 border-2 border-green-600 bg-gray-100' 
                                        : 'text-gray-800 border-2 border-gray-300 bg-transparent'}`}
                                onClick={() => setHighlightedImage('')}
                            >
                                No Picture
                            </div>
                            {/* Image Options */}
                            {images.map((image) => (
                                <div
                                    key={image}
                                    className={`w-[140px] h-[100px] flex justify-center items-center rounded overflow-hidden cursor-pointer 
                                        ${image === highlightedImage 
                                            ? 'border-2 border-green-600' 
                                            : 'border border-gray-300'}`}
                                    onClick={() => setHighlightedImage(image)}
                                >
                                    <img
                                        src={`/Profile_Thumbnails/${image}`}
                                        alt={image}
                                        className="max-w-full max-h-full object-cover"
                                        onError={(e) => {
                                            if (!e.target.complete || e.target.naturalWidth === 0) {
                                                console.error(`Error loading thumbnail: ${image}`);
                                            }
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="flex justify-center gap-5 py-2 bg-white">
                        <button
                            className="px-5 py-2 text-lg rounded bg-red-600 text-white"
                            onClick={onCancel}
                        >
                            Cancel
                        </button>
                        <button
                            className="px-5 py-2 text-lg rounded bg-green-600 text-white"
                            onClick={() => {
                                const fullSizeImage = highlightedImage.replace('_thumb', '');
                                onSelect(fullSizeImage);
                            }}
                        >
                            Select
                        </button>
                    </div>
                </div>
            </div>
        );    
    };

    // ------------------------------------
    // Main component rendering
    // ------------------------------------
    return (
        <div className="max-w-4xl mx-auto my-6 p-6 bg-white rounded-lg shadow-md">
            
            {!loading && isFreePlan === true && (
                <div className="text-sm text-gray-600 mb-4 bg-yellow-50 border border-yellow-300 p-3 rounded">
                    You’ve created {scenarioCount} of 3 custom scenarios allowed for free users.{" "}
                    <a
                    href="/pricing"
                    className="text-purple-700 underline hover:text-purple-900 ml-1"
                    >
                    Upgrade for more →
                    </a>
                </div>
            )}

            <h2 className="text-2xl font-semibold text-gray-800 mb-4">Custom Scenario Form</h2>
            <form className="space-y-6">

                {/* Scenario Title Section */}
                <div className="border border-gray-300 bg-white shadow-sm rounded-md p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <label
                        htmlFor="scenarioTitle"
                        className="text-base font-medium text-gray-800 mb-2 sm:mb-0 sm:mr-4"
                    >
                        Scenario Title:<span className="text-red-600 ml-1">*</span>
                    </label>
                    <div className="flex-1">
                        <input
                            id="scenarioTitle"
                            type="text"
                            name="scenarioTitle"
                            value={formData.scenarioTitle}
                            onChange={handleChange}
                            placeholder="Enter a title for the scenario"
                            className={`w-full p-2 border rounded text-sm ${
                                missingFields.scenarioTitle ? 'border-red-600' : 'border-gray-300'
                            }`}
                        />
                        {missingFields.scenarioTitle && (
                            <p className="text-red-600 text-sm mt-1">This field is required.</p>
                        )}
                    </div>
                </div>

                <div className="border border-gray-300 bg-white rounded-md p-6 mb-6 shadow-sm">
                    <h3 className="text-lg font-semibold mb-4 border-b pb-1">Employee and Job Details</h3>

                    {/* Employee Name */}
                    <div className="flex items-center mb-4">
                        <label
                            htmlFor="employeeName"
                            className={`text-base font-medium whitespace-nowrap mr-2 ${
                                missingFields.employeeName ? 'text-red-600' : 'text-gray-800'
                            }`}
                        >
                            Employee Name:<span className="text-red-600 ml-1">*</span>
                        </label>
                        <div className="flex-1">
                            <input
                            id="employeeName"
                            type="text"
                            name="employeeName"
                            value={formData.employeeName}
                            onChange={handleChange}
                            placeholder="Enter the employee's name"
                            className={`w-full p-2 border rounded-md ${
                                missingFields.employeeName ? 'border-red-500' : 'border-gray-300'
                            }`}
                            />
                            {missingFields.employeeName && (
                                <p className="text-red-500 text-sm mt-1">This field is required.</p>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:gap-6 mb-4">
                        {/* Approx. Age */}
                        <div className="flex items-center flex-1 mb-3 sm:mb-0">
                            <label
                            htmlFor="employeeAge"
                            className={`text-base font-medium whitespace-nowrap mr-2 ${
                                missingFields.employeeAge ? 'text-red-600' : 'text-gray-800'
                            }`}
                            >
                            Approx. Age:<span className="text-red-600 ml-1">*</span>
                            </label>
                            <div className="flex-1">
                            <input
                                id="employeeAge"
                                type="number"
                                name="employeeAge"
                                value={formData.employeeAge}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === "" || Number(value) >= 0) handleChange(e);
                                }}
                                onBlur={(e) => {
                                    const value = Number(e.target.value);
                                    if (value < 4) handleChange({ target: { name: "employeeAge", value: "4" } });
                                    else if (value > 110) handleChange({ target: { name: "employeeAge", value: "110" } });
                                }}
                                placeholder="Employee's age"
                                className={`w-full p-2 border rounded-md ${
                                    missingFields.employeeAge ? 'border-red-500' : 'border-gray-300'
                                }`}
                                min="4"
                                max="110"
                                step="1"
                            />
                            {missingFields.employeeAge && (
                                <p className="text-red-500 text-sm mt-1">This field is required.</p>
                            )}
                            </div>
                        </div>

                        {/* Job Title */}
                        <div className="flex items-center flex-1">
                            <label
                            htmlFor="jobTitle"
                            className={`text-base font-medium whitespace-nowrap mr-2 ${
                                missingFields.jobTitle ? 'text-red-600' : 'text-gray-800'
                            }`}
                            >
                            Job Title:<span className="text-red-600 ml-1">*</span>
                            </label>
                            <div className="flex-1">
                            <input
                                id="jobTitle"
                                type="text"
                                name="jobTitle"
                                value={formData.jobTitle}
                                onChange={handleChange}
                                placeholder="Employee's job title"
                                className={`w-full p-2 border rounded-md ${
                                missingFields.jobTitle ? 'border-red-500' : 'border-gray-300'
                                }`}
                            />
                            {missingFields.jobTitle && (
                                <p className="text-red-500 text-sm mt-1">This field is required.</p>
                            )}
                            </div>
                        </div>
                    </div>

                    {/* Profile Picture and Voice Selection Row */}
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-6 mb-6">
                        {/* Profile Picture Block */}
                        <div className="flex-1 border border-gray-300 rounded-md p-4 flex flex-col items-center justify-center text-center">
                            <label className="text-base font-medium text-gray-800 mb-3">
                            Profile Picture
                            </label>
                            <div className="flex items-center gap-3 justify-center">
                            <button
                                type="button"
                                className="bg-purple-700 text-white px-4 py-2 rounded hover:bg-purple-800 transition"
                                onClick={() => setIsImageModalOpen(true)}
                            >
                                Select
                            </button>
                            {formData.imageName ? (
                                <img
                                src={`/Profile_Thumbnails/${addThumbToFilename(formData.imageName)}`}
                                alt={formData.imageName}
                                className="w-12 h-12 object-cover rounded border"
                                />
                            ) : (
                                <div className="w-12 h-12 flex items-center justify-center text-xs text-gray-500 border rounded bg-gray-100">
                                No Picture
                                </div>
                            )}
                            </div>
                        </div>

                        {/* Voice Selection Block */}
                        <div className="flex-1 border border-gray-300 rounded-md p-4 flex flex-col items-center justify-center text-center">
                            <label className="text-base font-medium text-gray-800 mb-3">
                            Voice
                            </label>
                            <div className="flex gap-3">
                            {['male', 'female'].map((voice) => (
                                <button
                                key={voice}
                                type="button"
                                className={`px-4 py-2 border-2 rounded text-sm ${
                                    formData.voiceSelection === voice
                                    ? 'bg-green-600 text-white border-green-600'
                                    : 'bg-white text-gray-700 border-green-600'
                                }`}
                                onClick={() => setFormData({ ...formData, voiceSelection: voice })}
                                >
                                {voice.charAt(0).toUpperCase() + voice.slice(1)}
                                </button>
                            ))}
                            </div>
                        </div>
                    </div>

                    {/* Experience Details */}
                    <div className="border border-gray-300 rounded-md p-4 bg-white mb-6">
                        <div className="flex flex-wrap gap-4 justify-between">
                            {[
                            {
                                label: "Time at the Company",
                                name: "yearsAtCompany",
                                options: ['0-6 months', '6-12 months', '1-2 years', '2-4 years', '4-8 years', '8+ years'],
                                onChange: handleCompanyChange,
                                value: formData.yearsAtCompany,
                            },
                            {
                                label: "Time in Current Job",
                                name: "yearsInCurrentJob",
                                options: ['0-6 months', '6-12 months', '1-2 years', '2-4 years', '4-8 years', '8+ years'],
                                onChange: handleChange,
                                value: formData.yearsInCurrentJob,
                                disabledByIndex: true,
                            },
                            {
                                label: "Relevant Experience",
                                name: "totalExperience",
                                options: ['Less than 1 year', '1-2 years', '2-5 years', '5-10 years', '10-20 years', '20+ years'],
                                onChange: handleChange,
                                value: formData.totalExperience,
                            },
                            ].map((group) => (
                            <fieldset key={group.name} className="flex-1 min-w-[180px] mb-4">
                                <legend className="font-semibold mb-2">{group.label}:</legend>
                                {group.options.map((option, index) => {
                                const isDisabled =
                                    group.disabledByIndex &&
                                    group.name === 'yearsInCurrentJob' &&
                                    index > ['0-6 months', '6-12 months', '1-2 years', '2-4 years', '4-8 years', '8+ years'].indexOf(formData.yearsAtCompany);
                                return (
                                    <label key={option} className="flex items-center gap-2 mb-1">
                                    <input
                                        type="radio"
                                        name={group.name}
                                        value={option}
                                        checked={group.value === option}
                                        onChange={group.onChange}
                                        disabled={isDisabled}
                                    />
                                    {option}
                                    </label>
                                );
                                })}
                            </fieldset>
                            ))}
                        </div>
                    </div>

                    {/* Job Responsibilities */}
                    <label className="block font-medium text-gray-800 mb-1">
                        Key Job Responsibilities:
                    </label>
                    <textarea
                        name="jobResponsibilities"
                        value={formData.jobResponsibilities}
                        onChange={handleChange}
                        placeholder="Describe the employee's key job responsibilities"
                        className="w-full p-2 border border-gray-300 rounded-md resize-vertical"
                    />
                </div>

                {/* Scenario Description Section */}
                <div className="border border-gray-300 rounded-md p-6 bg-white mb-6">
                    <h3 className="text-lg font-semibold underline mb-4">Scenario Description</h3>

                    {/* Summary Field */}
                    {missingFields.performanceIssueSummary && (
                        <p className="text-red-500 text-sm mb-1">This field is required.</p>
                    )}
                    <label
                        htmlFor="performanceIssueSummary"
                        className={`block font-medium mb-1 ${missingFields.performanceIssueSummary ? 'text-red-600' : 'text-gray-800'}`}
                    >
                        General Summary:<span className="text-red-600 ml-1">*</span>
                    </label>
                    <textarea
                        id="performanceIssueSummary"
                        name="performanceIssueSummary"
                        value={formData.performanceIssueSummary}
                        onChange={handleChange}
                        placeholder="Summary of the issue or conversation topic"
                        className={`w-full p-3 rounded-md h-28 resize-vertical mb-4 ${
                        missingFields.performanceIssueSummary ? 'border-2 border-red-500' : 'border border-gray-300'
                        }`}
                    />

                    {/* Performance & Interpersonal Issues - Side by Side */}
                    <div className="flex flex-col lg:flex-row gap-6 mb-6">
                        {/* Performance Issues */}
                        <fieldset className="flex-1 border border-gray-300 rounded-md p-4">
                            <legend className="font-semibold mb-2">What performance issues have you observed?</legend>
                            {[
                            'Missed deadlines',
                            'Poor communication',
                            'Quality of work issues',
                            'Lack of engagement or initiative',
                            'Frequent mistakes / carelessness',
                            'Attendance issues',
                            'Resistant to feedback',
                            'Disruptive behavior',
                            'Other',
                            ].map((issue) => (
                            <label key={issue} className="block mb-1">
                                <input
                                type="checkbox"
                                name="performanceIssues"
                                value={issue}
                                checked={formData.performanceIssues.includes(issue)}
                                onChange={handleCheckboxChange}
                                className="mr-2"
                                />
                                {issue}
                            </label>
                            ))}
                            {formData.performanceIssues.includes("Other") && (
                            <textarea
                                name="performanceIssuesOther"
                                value={formData.performanceIssuesOther}
                                onChange={handleChange}
                                placeholder="Please specify other performance issues..."
                                className="w-full p-2 border border-gray-300 rounded mt-2"
                            />
                            )}
                        </fieldset>

                        {/* Interpersonal Issues */}
                        <fieldset
                            className={`flex-1 rounded-md p-4 ${
                            missingFields.interpersonalIssues ? 'border-2 border-red-500' : 'border border-gray-300'
                            }`}
                        >
                            <legend
                            className={`font-semibold mb-2 ${
                                missingFields.interpersonalIssues ? 'text-red-600' : 'text-gray-800'
                            }`}
                            >
                            Interpersonal issues with team members:<span className="text-red-600 ml-1">*</span>
                            </legend>
                            {[
                            'No known issues',
                            'Frequent disagreements with colleagues',
                            'Avoids collaboration with team members',
                            'Displays passive-aggressive behavior',
                            'Shows favoritism / exclusion within the team',
                            'Conflict with manager or leadership',
                            'Other',
                            ].map((issue) => (
                            <label key={issue} className="block mb-1">
                                <input
                                type="checkbox"
                                name="interpersonalIssues"
                                value={issue}
                                checked={formData.interpersonalIssues.includes(issue)}
                                onChange={handleCheckboxChange}
                                className="mr-2"
                                />
                                {issue}
                            </label>
                            ))}
                            {formData.interpersonalIssues.includes("Other") && (
                            <>
                                <textarea
                                name="interpersonalIssuesOther"
                                value={formData.interpersonalIssuesOther}
                                onChange={handleChange}
                                placeholder="Specify other interpersonal issues..."
                                className={`w-full p-2 rounded mt-2 ${
                                    missingFields.interpersonalIssuesOther ? 'border-2 border-red-500' : 'border border-gray-300'
                                }`}
                                />
                                {missingFields.interpersonalIssuesOther && (
                                <p className="text-red-500 text-sm mt-1">Please specify the "Other" issue.</p>
                                )}
                            </>
                            )}
                        </fieldset>
                    </div>


                    {/* Prior Discussions */}
                    <fieldset className="mb-6">
                        <legend className="font-semibold mb-2">Prior Discussions with the Employee:</legend>
                        {[
                        'No prior discussion has taken place.',
                        'The issue has been mentioned informally but not in detail.',
                        'There has been one formal conversation about this issue.',
                        'There have been multiple formal conversations about this issue.',
                        'There have been written warnings or documentation provided regarding this issue.',
                        ].map((option) => (
                        <label key={option} className="block mb-1">
                            <input
                            type="radio"
                            name="priorDiscussions"
                            value={option}
                            checked={formData.priorDiscussions === option}
                            onChange={handleChange}
                            className="mr-2"
                            />
                            {option}
                        </label>
                        ))}
                    </fieldset>

                    {/* Specific Examples + Auto Generate */}
                    <div className="border border-gray-300 rounded-md p-4 mb-6 bg-white">
                        <label className="block font-medium text-gray-800 mb-1">
                            Specific examples demonstrating issues:
                        </label>
                        <textarea
                            name="specificExamples"
                            value={formData.specificExamples}
                            onChange={handleChange}
                            disabled={formData.autoGenerateExamples}
                            placeholder="Provide specific examples"
                            className="w-full p-3 border border-gray-300 rounded mb-0 h-24"
                        />

                        <label className="flex items-center gap-2 font-medium">
                            <input
                            type="checkbox"
                            name="autoGenerateExamples"
                            checked={formData.autoGenerateExamples || false}
                            onChange={(e) =>
                                setFormData((prev) => ({
                                ...prev,
                                autoGenerateExamples: e.target.checked,
                                }))
                            }
                            />
                            Auto Generate Specific Examples
                            <ProfileTooltip profileKey="examples" />
                        </label>
                    </div>


                    {/* Additional Context */}
                    <fieldset className="mb-2">
                        <legend className="font-semibold mb-2">Other Relevant Information:</legend>
                        {[
                        'High-stress work environment',
                        'Frequent organizational changes causing uncertainty',
                        'Lack of clear expectations from leadership',
                        'Workload is too high',
                        'Lack of necessary skills or training',
                        'Employee resistance to change',
                        'Tension or conflict within the team',
                        'Employee dealing with personal stress (health, family, financial, etc.)',
                        'Other',
                        ].map((info) => (
                        <label key={info} className="block mb-1">
                            <input
                            type="checkbox"
                            name="additionalInfo"
                            value={info}
                            checked={formData.additionalInfo.includes(info)}
                            onChange={handleCheckboxChange}
                            className="mr-2"
                            />
                            {info}
                        </label>
                        ))}
                        {formData.additionalInfo.includes("Other") && (
                        <textarea
                            name="otherRelevantInfo"
                            value={formData.otherRelevantInfo}
                            onChange={handleChange}
                            placeholder="Please specify any other relevant context..."
                            className="w-full p-2 border border-gray-300 rounded mt-2"
                        />
                        )}
                    </fieldset>
                </div>

                {/* Employee Details Section */}
                <div className="border border-gray-300 bg-white rounded-md p-6 mb-8">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">
                        Employee Details
                    </h3>

                    {/* Understanding */}
                    <fieldset className="mb-6">
                        <legend className="font-medium text-gray-800 mb-2">
                            Employee’s Understanding of the Issue:
                        </legend>
                        <div className="flex flex-wrap justify-between gap-4">
                            {[
                            'Does not seem to understand the issue at all.',
                            'Has limited understanding and needs clarification.',
                            'Somewhat understands but may not see the full impact.',
                            'Fully understands the issue and its impact.',
                            ].map((value) => (
                            <label
                                key={value}
                                className="flex flex-col items-center text-center text-sm text-gray-700 w-full sm:w-[22%]"
                            >
                                <input
                                type="radio"
                                name="employeeUnderstanding"
                                value={value}
                                checked={formData.employeeUnderstanding === value}
                                onChange={handleChange}
                                className="mb-2"
                                />
                                {value}
                            </label>
                            ))}
                        </div>
                    </fieldset>

                    {/* Team Relationships */}
                    <fieldset className="mb-6">
                        <legend className="font-medium text-gray-800 mb-2">
                            How does the employee get along with their team or coworkers?
                        </legend>
                        <div className="flex flex-wrap justify-between gap-4">
                            {[
                                'Struggles with teamwork and frequently has conflicts.',
                                'Has had several interpersonal issues that have impacted teamwork.',
                                'Generally gets along but has occasional minor conflicts.',
                                'Works well with everyone and is a positive team member.',
                            ].map((value) => (
                            <label
                                key={value}
                                className="flex flex-col items-center text-center text-sm text-gray-700 w-full sm:w-[22%]"
                            >
                                <input
                                type="radio"
                                name="teamRelationships"
                                value={value}
                                checked={formData.teamRelationships === value}
                                onChange={handleChange}
                                className="mb-2"
                                />
                                {value}
                            </label>
                            ))}
                        </div>
                    </fieldset>

                    {/* Feelings */}
                    <fieldset
                        className={`mb-6 p-4 rounded ${
                        missingFields.employeeFeelings ? 'border-2 border-red-500' : 'border border-gray-300'
                        }`}
                    >
                        <legend
                        className={`font-medium mb-2 ${
                            missingFields.employeeFeelings ? 'text-red-600' : 'text-gray-800'
                        }`}
                        >
                        How does the employee feel about the situation? <span className="text-red-600">*</span>
                        </legend>
                        {[
                        'Motivated: The employee is ready to address the issue and sees this as an opportunity for growth.',
                        'Optimistic: The employee believes the situation will be resolved positively.',
                        'Confident: The employee feels capable of handling the situation.',
                        'Indifferent: The employee is disengaged or doesn’t seem to care about the situation.',
                        'Confused: The employee doesn’t fully understand the issue or its implications.',
                        'Frustrated: The employee feels stuck or overwhelmed by the situation.',
                        'Defensive: The employee feels blamed or attacked and may push back on feedback.',
                        'Anxious: The employee feels nervous or worried about the potential outcomes.',
                        'Resentful: The employee feels unfairly treated or is harboring negative feelings.',
                        'Other',
                        ].map((value) => (
                        <label key={value} className="block mb-2">
                            <input
                            type="checkbox"
                            name="employeeFeelings"
                            value={value}
                            checked={formData.employeeFeelings.includes(value)}
                            onChange={handleCheckboxChange}
                            className="mr-2"
                            />
                            {value}
                        </label>
                        ))}

                        {/* Free-text Other */}
                        {formData.employeeFeelings.includes("Other") && (
                        <>
                            <textarea
                            name="employeeFeelingsOther"
                            value={formData.employeeFeelingsOther}
                            onChange={handleChange}
                            placeholder="Please specify other feelings..."
                            className={`w-full p-2 rounded mt-2 ${
                                missingFields.interpersonalIssuesOther ? 'border-2 border-red-500' : 'border border-gray-300'
                            }`}
                            />
                            {missingFields.interpersonalIssuesOther && (
                            <p className="text-red-500 text-sm mt-1">Please specify the "Other" interpersonal issue.</p>
                            )}
                        </>
                        )}
                    </fieldset>

                    {/* Personal Factors */}
                    <fieldset className="mb-6">
                        <legend className="font-medium text-gray-800 mb-2">
                        Personal factors that may be impacting mood or productivity:
                        </legend>
                        {[
                        'Health concerns (physical or mental)',
                        'Family responsibilities or challenges',
                        'Financial stress',
                        'Recent personal loss or emotional distress',
                        'Work-life balance struggles',
                        'Relationship issues',
                        'Other',
                        ].map((value) => (
                        <label key={value} className="block mb-2">
                            <input
                            type="checkbox"
                            name="personalIssues"
                            value={value}
                            checked={formData.personalIssues.includes(value)}
                            onChange={handleCheckboxChange}
                            className="mr-2"
                            />
                            {value}
                        </label>
                        ))}

                        {formData.personalIssues.includes("Other") && (
                        <textarea
                            name="personalIssuesOther"
                            value={formData.personalIssuesOther}
                            onChange={handleChange}
                            placeholder="Please specify other personal factors..."
                            className="w-full p-2 border border-gray-300 rounded mt-2"
                        />
                        )}
                    </fieldset>

                    {/* Additional Details */}
                    <div className="mb-2">
                        <label className="block font-medium text-gray-800 mb-1">
                        Any other relevant information about the employee or situation:
                        </label>
                        <textarea
                        name="otherEmployeeDetails"
                        value={formData.otherEmployeeDetails}
                        onChange={handleChange}
                        placeholder="Any additional relevant context..."
                        className="w-full p-3 border border-gray-300 rounded h-24"
                        />
                    </div>
                </div>


                {/* Interpersonal Profile Factors */}
                <div className="border border-gray-300 bg-white p-6 rounded-md shadow mb-8">
                    <h3 className="text-xl font-semibold mb-4 border-b pb-2">Interpersonal Profile Factors</h3>

                    {/* Familiarity and Emotion */}
                    <div className="flex flex-col md:flex-row gap-6 mb-6">
                        {/* Familiarity */}
                        <div className="flex-1 border border-gray-300 rounded p-4">
                            <label className="block font-medium text-gray-800 mb-2">
                                Familiarity: <ProfileTooltip profileKey="familiarity" />
                                {isFreePlan && (
                                    <span className="ml-2 text-gray-500 text-sm" title="Upgrade to unlock this option">
                                        🔒
                                    </span>
                                )}
                            </label>
                            <div className="flex flex-col gap-3">
                                <div className="flex flex-wrap gap-2">
                                    {options.familiarity.map(({ value, label }) => (
                                        <button
                                            key={value}
                                            type="button"
                                            className={`px-4 py-2 border-2 rounded text-sm transition
                                                ${formData.familiarity === value
                                                    ? 'bg-green-600 text-white border-green-600'
                                                    : 'bg-white text-gray-700 border-green-600'}
                                                ${isFreePlan ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            onClick={() => {
                                                if (!isFreePlan) {
                                                    setFormData({ ...formData, familiarity: value });
                                                }
                                            }}
                                            disabled={isFreePlan}
                                            >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                                <div className="italic text-sm text-gray-600">
                                    {options.familiarity.find(({ value }) => value === formData.familiarity)?.description}
                                </div>

                                {isFreePlan && (
                                    <a
                                        href="/pricing"
                                        className="text-xs text-amber-600 italic mt-2 block hover:text-amber-700"
                                    >
                                        This setting is available with a paid subscription.
                                    </a>
                                )}
                            </div>
                        </div>

                        {/* Emotion */}
                        <div className="flex-1 border border-gray-300 rounded p-4">
                            <label className="block font-medium text-gray-800 mb-2">
                                Emotion: <ProfileTooltip profileKey="emotion" />
                                {isFreePlan && (
                                    <span className="ml-2 text-gray-500 text-sm" title="Upgrade to unlock this option">
                                        🔒
                                    </span>
                                )}
                            </label>
                            <div className="flex flex-col gap-3">
                                <div className="flex flex-wrap gap-2">
                                    {options.emotion.map(({ value, label }) => (
                                        <button
                                            key={value}
                                            type="button"
                                            className={`px-4 py-2 border-2 rounded text-sm transition
                                                ${formData.emotion === value
                                                    ? 'bg-green-600 text-white border-green-600'
                                                    : 'bg-white text-gray-700 border-green-600'}
                                                ${isFreePlan ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            onClick={() => {
                                                if (!isFreePlan) {
                                                    setFormData({ ...formData, emotion: value });
                                                }
                                            }}
                                            disabled={isFreePlan}
                                            >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                                <div className="italic text-sm text-gray-600">
                                    {options.emotion.find(({ value }) => value === formData.emotion)?.description}
                                </div>

                                {isFreePlan && (
                                    <a
                                        href="/pricing"
                                        className="text-xs text-amber-600 italic mt-2 block hover:text-amber-700"
                                    >
                                        This setting is available with a paid subscription.
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Trust Section */}
                    <div className="border border-gray-300 rounded p-4">
                        <label className="block font-medium text-gray-800 mb-2">
                            Trust: <ProfileTooltip profileKey="trust" />
                            {isFreePlan && (
                                <span className="ml-2 text-gray-500 text-sm" title="Upgrade to unlock this option">
                                    🔒
                                </span>
                            )}
                        </label>
                        <div className="flex flex-col gap-4">
                            <div className="flex flex-col sm:flex-row gap-6">
                                {/* Logical */}
                                <div className="flex-1">
                                    <label className="block font-semibold mb-2">Logical:</label>
                                    <div className="flex gap-2 mb-1">
                                        {options.trustCognitive.map(({ value, label }) => (
                                            <button
                                                key={value}
                                                type="button"
                                                className={`px-4 py-2 border-2 rounded text-sm transition
                                                    ${formData.trustCognition === value
                                                        ? 'bg-green-600 text-white border-green-600'
                                                        : 'bg-white text-gray-700 border-green-600'}
                                                    ${isFreePlan ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                onClick={() => {
                                                    if (!isFreePlan) {
                                                        const cognitionValue = (['low', 'medium', 'high'].indexOf(value) + 1).toString();
                                                        const emotionValue = formData.trustEmotionNumeric;
                                                        const newTrust = trustMapping[cognitionValue + emotionValue];
                                                        setFormData({
                                                            ...formData,
                                                            trustCognition: value,
                                                            trustCognitionNumeric: cognitionValue,
                                                            trust: newTrust,
                                                        });
                                                    }
                                                }}
                                                disabled={isFreePlan}
                                                >
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="italic text-sm text-gray-600">
                                        {options.trustCognitive.find(({ value }) => value === formData.trustCognition)?.description}
                                    </div>

                                    {isFreePlan && (
                                        <a
                                            href="/pricing"
                                            className="text-xs text-amber-600 italic mt-2 block hover:text-amber-700"
                                        >
                                            This setting is available with a paid subscription.
                                        </a>
                                    )}
                                </div>

                                {/* Emotional */}
                                <div className="flex-1">
                                    <label className="block font-semibold mb-2">Emotional:</label>
                                    <div className="flex gap-2 mb-1">
                                        {options.trustEmotion.map(({ value, label }) => (
                                            <button
                                                key={value}
                                                type="button"
                                                className={`px-4 py-2 border-2 rounded text-sm transition
                                                    ${formData.trustEmotion === value
                                                        ? 'bg-green-600 text-white border-green-600'
                                                        : 'bg-white text-gray-700 border-green-600'}
                                                    ${isFreePlan ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                onClick={() => {
                                                    if (!isFreePlan) {
                                                        const emotionValue = (['low', 'medium', 'high'].indexOf(value) + 1).toString();
                                                        const cognitionValue = formData.trustCognitionNumeric;
                                                        const newTrust = trustMapping[cognitionValue + emotionValue];
                                                        setFormData({
                                                            ...formData,
                                                            trustEmotion: value,
                                                            trustEmotionNumeric: emotionValue,
                                                            trust: newTrust,
                                                        });
                                                    }
                                                }}
                                                disabled={isFreePlan}
                                                >
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="italic text-sm text-gray-600">
                                        {options.trustEmotion.find(({ value }) => value === formData.trustEmotion)?.description}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>


                {/* Personality Profiles - Values */}
                <div className="border border-gray-300 bg-white p-6 rounded-md shadow mb-8">
                    <h3 className="text-xl font-semibold mb-4 border-b pb-2">
                        Personality Profile
                    </h3>

                    {/* Values Selection */}
                    <div className="border border-gray-300 rounded p-4 mb-8">
                        <h3 className="text-lg font-semibold mb-4 border-b pb-2">
                            Values
                            {isFreePlan && (
                                <span className="ml-2 text-gray-500 text-sm" title="Upgrade to unlock this option">
                                    🔒
                                </span>
                            )}
                        </h3>
                        <div className="flex flex-col sm:flex-row gap-6">
                            {/* Means */}
                            <div className="flex-1">
                                <label className="block font-medium text-gray-800 mb-2">Means:</label>
                                <div className="flex gap-2">
                                    {options.valuesMeans.map(({ value, label }) => (
                                    <button
                                        key={`means-${value}`}
                                        type="button"
                                        className={`px-4 py-2 border-2 rounded text-sm transition
                                            ${formData.valuesMeans === value
                                                ? 'bg-green-600 text-white border-green-600'
                                                : 'bg-white text-gray-700 border-green-600'}
                                            ${isFreePlan ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        onClick={() => {
                                            if (!isFreePlan) {
                                                const ends = formData.valuesEnds;
                                                const newVal = valuesMapping[value + ends];
                                                setFormData({ ...formData, valuesMeans: value, values: newVal });
                                            }
                                        }}
                                        disabled={isFreePlan}
                                    >
                                        {label}
                                    </button>
                                    ))}
                                </div>
                                <div className="text-sm text-gray-600 italic mt-2">
                                    {
                                    options.valuesMeans.find(({ value }) => value === formData.valuesMeans)
                                        ?.description
                                    }
                                </div>
                                {isFreePlan && (
                                    <a
                                        href="/pricing"
                                        className="text-xs text-amber-600 italic mt-2 block hover:text-amber-700"
                                    >
                                        This setting is available with a paid subscription.
                                    </a>
                                )}
                            </div>

                            {/* Ends */}
                            <div className="flex-1">
                                <label className="block font-medium text-gray-800 mb-2">Ends:</label>
                                <div className="flex gap-2">
                                    {options.valuesEnds.map(({ value, label }) => (
                                    <button
                                        key={`ends-${value}`}
                                        type="button"
                                        className={`px-4 py-2 border-2 rounded text-sm transition
                                            ${formData.valuesEnds === value
                                                ? 'bg-green-600 text-white border-green-600'
                                                : 'bg-white text-gray-700 border-green-600'}
                                            ${isFreePlan ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        onClick={() => {
                                            if (!isFreePlan) {
                                                const means = formData.valuesMeans;
                                                const newVal = valuesMapping[means + value];
                                                setFormData({ ...formData, valuesEnds: value, values: newVal });
                                            }
                                        }}
                                        disabled={isFreePlan}
                                    >
                                        {label}
                                    </button>
                                    ))}
                                </div>
                                <div className="text-sm text-gray-600 italic mt-2">
                                    {
                                    options.valuesEnds.find(({ value }) => value === formData.valuesEnds)
                                        ?.description
                                    }
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Disposition Sliders Header */}
                    <h3 className="text-lg font-semibold mb-4 border-b pb-2">
                        Disposition
                    </h3>

                    {/* Sliders Section Container */}
                    <div className="space-y-6">
                        {/* START SLIDERS */}
                        <div className="mb-8 mt-6">
                        {Object.keys(sliderOptions).map((profile) => {
                            const selectedValue = parseInt(formData[profile], 10);
                            const profileOption = sliderOptions[profile][selectedValue];
                            const profileDetails = {
                                ew: { left: "Engage", right: "Withdraw" },
                                ac: { left: "Accept", right: "Control" },
                                ei: { left: "Extrovert", right: "Introvert" },
                                di: { left: "Direct", right: "Indirect" },
                                pj: { left: "Perceive", right: "Judge" },
                                rt: { left: "Risk-Take", right: "Risk-Assess" },
                                op: { left: "Optimist", right: "Pessimist" },
                                co: { left: "Change-Oriented", right: "Continuity-Oriented" },
                                tp: { left: "Task-Focused", right: "People-Focused" }
                            }[profile];

                            const labelOffset = (selectedValue - 1) * 25;
                            const labelStyle = {
                                position: 'relative',
                                left: selectedValue === 1 ? '0%' : selectedValue === 5 ? '100%' : `${labelOffset}%`,
                                transform: selectedValue === 1 ? 'none' : selectedValue === 5 ? 'translateX(-100%)' : 'translateX(-50%)',
                                fontWeight: 'bold',
                                fontSize: '16px',
                                color: '#6c63ff',
                                padding: '5px 10px',
                                borderRadius: '4px',
                                textAlign: 'center',
                                display: 'inline-block',
                                width: 'fit-content',
                            };

                            return (
                                <div key={profile} className="bg-white rounded-lg p-4 mb-6 shadow-md">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', fontWeight: 'bold' }}>
                                        <span>{profileDetails.left}</span>
                                        <span>{profileDetails.right}</span>
                                    </div>
                                    <div style={{ position: 'relative', height: '40px' }}>
                                        <input
                                            type="range"
                                            name={profile}
                                            min="1"
                                            max="5"
                                            value={selectedValue}
                                            onChange={(e) => setFormData({ ...formData, [profile]: parseInt(e.target.value, 10) })}
                                            disabled={isFreePlan && !['ew', 'ac', 'ei'].includes(profile)}
                                            style={{
                                                ...styles.slider,
                                                background:
                                                    selectedValue === 1
                                                    ? `linear-gradient(to right, #81c345 25%, grey 50%)`
                                                    : selectedValue === 2
                                                    ? `linear-gradient(to right, grey 0%, grey 25%, #81c345 25%, grey 50%)`
                                                    : selectedValue === 3
                                                    ? `linear-gradient(to right, grey 0%, grey 100%)`
                                                    : selectedValue === 4
                                                    ? `linear-gradient(to left, grey 0%, grey 25%, #81c345 25%, grey 50%)`
                                                    : `linear-gradient(to left, #81c345 25%, grey 50%)`,
                                                opacity: isFreePlan && !['ew', 'ac', 'ei'].includes(profile) ? 0.5 : 1,
                                                cursor: isFreePlan && !['ew', 'ac', 'ei'].includes(profile) ? 'not-allowed' : 'pointer',
                                            }}
                                        />
                                        <div style={styles.tickContainer}>
                                            {[1, 2, 3, 4, 5].map((tick, index) => (
                                            <div key={tick} style={{ ...styles.tick, left: `${1.5 + index * 24.25}%` }}>^</div>
                                            ))}
                                        </div>
                                    </div>
                                    <div style={labelStyle}>{profileOption?.label}</div>
                                    {profileOption && (
                                        <div style={styles.description}>
                                            <ProfileTooltip profileKey={profile} /> {profileOption.description}
                                        </div>
                                    )}
                                    {isFreePlan && !['ew', 'ac', 'ei'].includes(profile) && (
                                        <a
                                            href="/pricing"
                                            className="text-xs text-amber-600 italic mt-2 flex items-center gap-1 hover:text-amber-700"
                                        >
                                            <span role="img" aria-label="lock">🔒</span>
                                            This setting is available with a paid subscription.
                                        </a>
                                    )}
                                </div>
                            );
                        })}
                        </div>
                    </div>
                </div>

                {/* DISC Calculator */}
                <div className="border border-gray-300 rounded p-6 bg-white shadow mt-8">
                    <h3 className="text-lg font-semibold mb-4 underline">Calculate DISC Profile</h3>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 bg-gray-50 p-4 rounded">
                        {/* Button */}
                        <button
                        type="button"
                        className="bg-purple-700 text-white px-6 py-2 rounded hover:bg-purple-800 transition w-full sm:w-auto"
                        onClick={handleCalculateDISC}
                        >
                        Calculate DISC Type
                        </button>

                        {/* DISC Profile Text */}
                        {calculatedDisc && (
                        <div className="flex-1">
                            <h4 className="text-base font-semibold text-gray-800">
                            DISC Profile: <span className="font-normal text-gray-700">{calculatedDisc.label}</span>
                            </h4>
                            <p className="text-sm text-gray-600 mt-1">
                            Description: <span className="text-gray-700">{calculatedDisc.description}</span>
                            </p>
                        </div>
                        )}
                    </div>
                </div>


                {Object.keys(missingFields).length > 0 && (
                    <p className="text-red-600 text-sm mb-4">
                        Please complete the following required fields:{" "}
                        {Object.keys(missingFields)
                        .map((field) => fieldLabelMapping[field] || field)
                        .join(", ")}
                    </p>
                )}

                {/* Submit Buttons */}
                {isFreePlan && scenarioCount >= 3 ? (
                    <div className="mt-6 flex items-center gap-4">
                        <button
                            type="button"
                            onClick={() => handleFormValidation('new')}
                            disabled={isFreePlan && scenarioCount >= 3}
                            className={`px-4 py-2 rounded text-white transition
                            ${isFreePlan && scenarioCount >= 3 
                                ? 'bg-gray-400 cursor-not-allowed' 
                                : 'bg-green-600 hover:bg-green-700'}`}
                        >
                            Create Scenario
                        </button>

                        {isFreePlan && scenarioCount >= 3 && (
                            <span className="text-sm text-red-600 font-medium max-w-xs">
                                You’ve reached your limit of 3 custom scenarios.{" "}
                                <a
                                    href="/pricing"
                                    className="underline hover:text-red-700 transition"
                                >
                                    Upgrade subscription
                                </a>{" "}
                                to create more.
                            </span>
                        )}
                    </div>
                ) : isEdit ? (
                    <div className="flex gap-4 mt-6">
                        <button
                            type="button"
                            onClick={() => handleFormValidation('new')}
                            className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                            >
                            Create New Scenario
                        </button>
                        <button
                            type="button"
                            onClick={() => handleFormValidation('update')}
                            className="flex-1 px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
                            >
                            Update Existing Scenario
                        </button>
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={() => handleFormValidation('new')}
                        className="mt-6 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                        Create Scenario
                    </button>
                )}
            </form>

            {isImageModalOpen && (
                <ImageSelectionModal
                    images={imageList}
                    selectedImage={formData.imageName}
                    onSelect={(imageName) => {
                    const fullSizeImage = imageName.replace('_thumb', '');
                    setFormData({ ...formData, imageName: fullSizeImage });
                    setIsImageModalOpen(false);
                    }}
                    onCancel={() => setIsImageModalOpen(false)}
                />
            )}

            {/* Modal for Progress */}
            {isLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-lg w-80 text-center shadow-lg">
                        <h4 className="text-lg font-medium mb-4">Processing, please wait...</h4>
                        <div className="w-full bg-gray-300 rounded overflow-hidden h-5">
                            <div
                            className="bg-green-600 h-full transition-all duration-500"
                            style={{ width: `${progress}%` }}
                            />
                        </div>
                        <p className="text-sm mt-2">{progress}% completed</p>
                    </div>
                </div>
            )}

            {/* Modal for Confirmation */}
            {showConfirmModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-lg text-center">
                    <h3 className="text-lg font-semibold mb-4">Confirm Scenario Creation</h3>
                        <p className="mb-4">
                            {pendingActionType === 'confirm-rename' ? (
                                <>
                                    You are creating a new scenario based on an existing one, but the title has not been changed.
                                    <br /><br />
                                    <strong>Consider updating the title</strong> so you can more easily distinguish between scenarios.
                                    <br /><br />
                                    You can change the title below, or proceed with the current name.
                                </>
                            ) : (
                                <>
                                Are you sure you want to {pendingActionType === 'new' ? 'create a new' : 'update this'} scenario?
                                {isFreePlan && (
                                    <span className="block mt-2 text-sm text-gray-600">
                                    You have used {scenarioCount} of 3 scenarios allowed on the free plan.
                                    </span>
                                )}
                                </>
                            )}
                        </p>

                        {pendingActionType === 'confirm-rename' && (
                            <input
                                type="text"
                                value={formData.scenarioTitle}
                                onChange={(e) =>
                                setFormData({ ...formData, scenarioTitle: e.target.value })
                                }
                                className="w-full border rounded px-3 py-2 text-gray-700 mt-2 mb-4"
                                placeholder="Enter a new scenario title"
                            />
                        )}

                    <div className="flex justify-center gap-4 mt-4">
                        <button
                        onClick={() => setShowConfirmModal(false)}
                        className="px-4 py-2 border rounded text-gray-700 hover:bg-gray-100"
                        >
                        Cancel
                        </button>
                        <button
                        onClick={() => {
                            setShowConfirmModal(false);
                            if (pendingActionType === 'new' || pendingActionType === 'confirm-rename') handleCreateNewScenario();
                            else if (pendingActionType === 'update') handleUpdateExistingScenario();
                        }}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                        >
                        Proceed
                        </button>
                    </div>
                </div>
            </div>
            )}

            {/* Modal for Limit Exceeded */}
            {limitExceededMessage && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded shadow-md max-w-md w-full text-center">
                        <h3 className="text-lg font-semibold mb-2 text-red-600">Limit Reached</h3>
                        <p className="mb-4">{limitExceededMessage}</p>
                        <button
                            className="bg-gray-700 text-white px-4 py-2 rounded hover:bg-gray-800"
                            onClick={() => setLimitExceededMessage('')}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

        </div>    
    );
}

export default CreateScenarioForm;
